import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Application } from 'src/app/models/application.model';
import { ApplicationEnvironment } from 'src/app/models/application_environment.model';
import { ApplicationType } from 'src/app/models/application_type.model';
import { ProgrammingTag } from 'src/app/models/programming_tag.model';
import { ApiService } from 'src/app/services/api.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatPrefix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-company-product-application-credit',
  templateUrl: './company-product-application-credit.component.html',
  styleUrls: ['./company-product-application-credit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatOption,
    MatPrefix,
    MatSelect,
    MatIconButton,
    AsyncPipe,
  ],
})
export class CompanyProductApplicationCreditComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  protected fb = inject(FormBuilder);
  private api = inject(ApiService);
  protected router = inject(Router);
  private snack = inject(MatSnackBar);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  isGithubRepo: boolean = false;
  app_id: string | undefined | null;
  private company_product_id: string;

  get showDeployWorkflowPath(): boolean {
    return this.isGithubRepo || (this.form.value['git_url']?.includes('github.com') ?? false);
  }

  form: FormGroup;
  appTypeAutocompleteCtrl: FormControl = new FormControl();
  appProgrammingTagAutocompleteCtrl: FormControl = new FormControl();

  private application_types: ApplicationType[] = [];
  filtered_app_types: Observable<ApplicationType[]>;
  private programming_tags: ProgrammingTag[] = [];
  filtered_programming_tags: Observable<ProgrammingTag[]>;

  app: Application | undefined;
  environments: ApplicationEnvironmentEditable[];
  checking_github = false;
  force_show_github_install = false;

  get isCreate() {
    return this.app_id == null;
  }

  get showGithubInstallButton(): boolean {
    return !this.isCreate && this.isGithubRepo;
  }

  get githubInstallButtonLabel(): string {
    if (this.app?.github_token_set && !this.force_show_github_install) {
      return 'Verificar / renovar GitHub App';
    }

    return this.app?.github_token_set ? 'Renovar / actualizar permisos GitHub' : 'Instalar Bisual App en GitHub';
  }

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(5)])],
      git_url: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(/[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/),
        ]),
      ],
      deploy_workflow_path: ['.github/workflows/deploy.yml'],
      application_types: [[]],
      programming_tags: [[]],
    });
  }

  ngOnInit(): void {
    this.initPageCondition();
    if (!this.isCreate) this.fetchApp();
    this.fetchApplicationTypes();
    this.fetchProgrammingTags();
    this.listenAutocompleteControls();
    this.handleGithubSetupQueryParam();

    // Update isGithubRepo when git_url changes
    this.form.get('git_url')?.valueChanges.subscribe(gitUrl => {
      this.isGithubRepo = gitUrl?.includes('github.com') ?? false;
    });
  }

  removeAppType(index: number) {
    const app_types: ApplicationType[] = this.form.value['application_types'];
    app_types.splice(index, 1);
    this.form.patchValue({ application_types: app_types });
  }

  addAppType(event: MatAutocompleteSelectedEvent) {
    const type: ApplicationType = event.option.value;
    const types: ApplicationType[] = this.form.value['application_types'];
    types.push(type);
    this.form.patchValue({ application_types: types });
    this.appTypeAutocompleteCtrl.reset();
  }

  removeProgrammingTag(index: number) {
    const tags: ApplicationType[] = this.form.value['programming_tags'];
    tags.splice(index, 1);
    this.form.patchValue({ programming_tags: tags });
  }

  addProgrammingTag(event: MatAutocompleteSelectedEvent) {
    const tag: ProgrammingTag = event.option.value;
    const tags: ProgrammingTag[] = this.form.value['programming_tags'];
    tags.push(tag);
    this.form.patchValue({ programming_tags: tags });
    this.appProgrammingTagAutocompleteCtrl.reset();
  }

  submit() {
    const body: any = { ...this.form.value };

    body.application_type_ids = body.application_types.map((type: ApplicationType) => type.id);
    delete body.application_types;

    body.programming_tag_ids = body.programming_tags.map((tag: ProgrammingTag) => tag.id);
    delete body.programming_tags;

    body.company_product_id = this.company_product_id;
    let obs: Observable<Application> = null as any;
    if (this.isCreate) {
      obs = this.api.createApplication(body);
    } else {
      obs = this.api.updateApplication(this.app_id as string, body);
    }

    obs.subscribe(data => {
      this.router.navigate(['../'], { relativeTo: this.activatedRoute });
    });
  }

  checkGithub() {
    if (this.checking_github || this.app_id == null) {
      return;
    }

    this.checking_github = true;
    this.api.checkApplicationGithubConnection(this.app_id.toString()).subscribe({
      next: data => {
        this.checking_github = false;
        if (data.status) {
          this.force_show_github_install = false;
          this.fetchApp(() => {
            this.snack.open('Conexión correcta. Token de GitHub configurado.', 'Ok', { duration: 4000 });
          });
          return;
        }

        this.force_show_github_install = true;
        const install_url = data.install_url ?? 'https://github.com/apps/bisual/installations/new';
        window.open(install_url, '_blank');
        this.snack.open(
          data.message ?? 'Abre la instalación de GitHub. Tras aceptar, volverás automáticamente a esta pantalla con el token guardado.',
          'Ok',
          { duration: 7000 }
        );
      },
      error: () => {
        this.checking_github = false;
        this.force_show_github_install = true;
        window.open('https://github.com/apps/bisual/installations/new', '_blank');
        this.snack.open('No se pudo verificar GitHub. Abre la instalación manualmente y, tras aceptar, vuelve a pulsar el botón.', 'Ok', {
          duration: 7000,
        });
      },
    });
  }

  saveEnv(index: number) {
    this.environments[index].editing = false;
    const env = this.environments[index];

    let promiseEnv: Observable<ApplicationEnvironment> = null as any;
    env.application_id = this.app_id as string;
    if (env.id != null) {
      // és editar
      promiseEnv = this.api.updateApplicationEnvironment(env.id, env);
    } else {
      promiseEnv = this.api.createApplicationEnvironment(env);
    }

    promiseEnv.subscribe(data => {
      // ha anat ok...
    });
  }

  deleteEnv(index: number) {
    if (confirm('¿Estás seguro que quieres eliminar este entorno de aplicación?')) {
      const env: ApplicationEnvironmentEditable = this.environments.splice(index, 1)[0];
      if (env.id != null) {
        this.api.deleteApplicationEnvironment(env.id).subscribe(
          () => {},
          () => {
            this.environments.push(env);
          }
        );
      }
    }
  }

  addEnv() {
    this.environments.push({
      id: undefined as any,
      application_id: '',
      role: '' as any,
      git_ref: '',
      url: '',
      editing: true,
    } as any);
  }

  validEnv(item: ApplicationEnvironmentEditable): boolean {
    return item.git_ref != '' && (item.role as any) != '' && item.url != '';
  }

  private initPageCondition() {
    this.app_id = this.activatedRoute.snapshot.paramMap.get('app_id');
    this.company_product_id = this.activatedRoute.snapshot.parent?.parent?.paramMap.get('id') as string;
  }

  private handleGithubSetupQueryParam(): void {
    const github_setup = this.activatedRoute.snapshot.queryParamMap.get('github_setup');
    if (github_setup === 'ok') {
      this.force_show_github_install = false;
      this.snack.open('GitHub App instalada. Token guardado correctamente.', 'Ok', { duration: 5000 });
      this.fetchApp();
      return;
    }

    if (github_setup === 'error') {
      this.force_show_github_install = true;
      this.snack.open('La instalación en GitHub no se completó (permisos insuficientes o error). Pulsa el botón para reintentar.', 'Ok', {
        duration: 7000,
      });
    }
  }

  private fetchApp(on_loaded?: () => void) {
    this.api.getApplication(this.app_id as string, { with: 'application_types,programming_tags,environments' }).subscribe(data => {
      this.app = data;
      // Update form values individually to ensure validations work correctly
      this.form.patchValue(
        {
          name: data.name || '',
          git_url: data.git_url || '',
          deploy_workflow_path: data.deploy_workflow_path || '.github/workflows/deploy.yml',
          application_types: data.application_types || [],
          programming_tags: data.programming_tags || [],
        },
        { emitEvent: false }
      );
      // Mark form as touched to show validation errors if any
      this.form.markAllAsTouched();
      this.isGithubRepo = data.git_url.includes('github.com');
      this.environments = (data.environments as ApplicationEnvironmentEditable[]).map(e => {
        e.editing = false;
        return e;
      });
      on_loaded?.();
    });
  }

  private fetchApplicationTypes() {
    this.api.getApplicationTypes().subscribe(data => {
      this.application_types = data;
    });
  }

  private fetchProgrammingTags() {
    this.api.getProgrammingTags().subscribe(data => {
      this.programming_tags = data;
    });
  }

  private listenAutocompleteControls() {
    this.filtered_app_types = this.appTypeAutocompleteCtrl.valueChanges.pipe(
      startWith(null),
      map((search_str: string | null) =>
        this.application_types
          .filter(type => (this.form.value['application_types'] as ApplicationType[]).findIndex(type2 => type2.id == type.id) == -1)
          .filter(type => !search_str || typeof search_str !== 'string' || type.name.toLowerCase().includes(search_str.toLowerCase()))
      )
    );

    this.filtered_programming_tags = this.appProgrammingTagAutocompleteCtrl.valueChanges.pipe(
      startWith(null),
      map((search_str: string | null) =>
        this.programming_tags
          .filter(tag => (this.form.value['programming_tags'] as ProgrammingTag[]).findIndex(tag2 => tag2.id == tag.id) == -1)
          .filter(tag => !search_str || typeof search_str !== 'string' || tag.name.toLowerCase().includes(search_str.toLowerCase()))
      )
    );
  }
}

interface ApplicationEnvironmentEditable extends ApplicationEnvironment {
  editing: boolean;
}
